import { FEAT, SKILLS, formFx } from '@somnhold/content';
import { dist2, idiv, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, UqDue, Zone } from '../types.ts';
import { damageEnemy, enemiesNear, ev, healHero, targetable } from './combat.ts';
import { getUp } from './dungeon.ts';
import { featPieceAlly, featPieceTest, featPieceWatch, featUse } from './feats.ts';
import { formFinal } from './forms.ts';
import { bigOne, due, heroById, pieceStun } from './panoply.ts';
import { enemiesInLine, healPower, heroesInLine, inspire, mostHurt, poison, power } from './skills.ts';
import { fx, hasUq } from './uniques.ts';

/**
 * The cleric's panoply in a fight (docs/PANOPLIA.md §10), as battle/panoply.ts does the warrior's. Most of the
 * cleric's pieces change what happens to an ally (a renewal, a choir, an intercession): the ally keeps whose it was in
 * `h.uqT`, by key, and the piece's owner is found by it.
 */

const kept = (h: Hero): Record<string, number> => (h.uqT ??= {});

/** The nearest standing nightmare to a point within `r`, but those in `skip` (by id on ties). */
const nearestBut = (s: BattleState, x: number, y: number, r: number, skip: readonly number[]): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const o of s.enemies) {
    if (o.hp <= 0 || skip.includes(o.id) || !targetable(s, o) || o.kind === 'boneWall' || !withinRange(o.x, o.y, x, y, r)) continue;
    const d = dist2(o.x, o.y, x, y);
    if (d < bd || (d === bd && best && o.id < best.id)) {
      bd = d;
      best = o;
    }
  }
  return best;
};

const below = (h: Hero, bp: number): boolean => h.hp * 10000 < h.stats.maxHp * bp;

/** Holy ground the pieces leave: a sanctuary of the cleric's, `healBp` as strong. */
const holyGround = (s: BattleState, h: Hero, x: number, y: number, radius: number, ticks: number, healBp: number): void => {
  const sk = SKILLS.sanctuary;
  s.zones.push({ id: s.nextId++, kind: 'sanctuary', x, y, radius, cells: [], ticks, every: sk.every, damage: 0, slowBp: 0, pid: h.pid, heal: mulBp(healPower(h, sk.amount), healBp) } as Zone);
  ev(s, { t: 'boom', x, y, r: radius, kind: 'holyGround' });
};

/** One bad state off a hero (Limpia): the first it has of stun, fear, sleep, curse, poison. */
const cleanOne = (o: Hero): void => {
  if ((o.stun ?? 0) > 0) o.stun = 0;
  else if ((o.fear ?? 0) > 0) o.fear = 0;
  else if ((o.sleep ?? 0) > 0) o.sleep = 0;
  else if ((o.curse ?? 0) > 0) o.curse = 0;
  else if ((o.poison ?? 0) > 0) o.poison = 0;
};

/* ---------- Bastón de la enfermera (Sanar) ---------- */

/**
 * The mend landed on `a` (its life before it, `was`): on one below `belowBp` it recharges at once (once every `every`
 * ticks); what it healed there is counted, and one saved from below `saveBp` is watched. Returns the new wait, or −1.
 */
export const nurseMend = (s: BattleState, h: Hero, a: Hero, was: number): number => {
  if (!hasUq(h, 'nurseStaff')) return -1;
  const f = fx('nurseStaff', h);
  if (formFinal(h, 'nurseStaff')) for (const o of s.heroes) if (o.id !== a.id && o.downTicks === 0 && withinRange(o.x, o.y, a.x, a.y, formFx('nurseStaff', 'final').radius as number)) inspire(o);
  if (was * 10000 >= a.stats.maxHp * (f.belowBp as number)) return -1;
  featUse(s, h, 'nurseStaff', a.hp - was);
  if (was * 10000 < a.stats.maxHp * FEAT.need.saveBp && a.id !== h.id) featPieceWatch(s, h, 'nurseStaff', [a.id], FEAT.watch.nurse, -1);
  const q = kept(h);
  if ((q.nurseAt ?? 0) > s.tick) return -1;
  q.nurseAt = s.tick + (f.every as number);
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 500, kind: 'nurseAgain' });
  return 0;
};

/* ---------- Bastón del aguatero (Rayo de fe) ---------- */

/**
 * The ray stays on the ground: every `every` it hurts and heals what crosses it again (`bp` of the first). With Dos rayos
 * a second crosses it through its middle, at once, and stays too. `hits` and `mended` are what the first ray reached.
 */
export const rayStays = (s: BattleState, h: Hero, ex: number, ey: number, w: number, dmg: number, heal: number, hits: Enemy[], mended: Hero[]): void => {
  if (!hasUq(h, 'watercarrierStaff')) return;
  const f = fx('watercarrierStaff', h);
  const lines: [number, number, number, number][] = [[h.x, h.y, ex, ey]];
  const foes = hits.map((o) => o.id);
  const allies = mended.map((o) => o.id);
  if (formFinal(h, 'watercarrierStaff')) {
    // Across, through the middle, as long.
    const mx = idiv(h.x + ex, 2);
    const my = idiv(h.y + ey, 2);
    const hx = idiv(ey - h.y, 2);
    const hy = idiv(h.x - ex, 2);
    const l: [number, number, number, number] = [mx - hx, my - hy, mx + hx, my + hy];
    lines.push(l);
    rayPulse(s, h, l, w, dmg, heal, foes, allies);
    ev(s, { t: 'beam', x: l[0], y: l[1], tx: l[2], ty: l[3], kind: 'faithRay' });
  }
  for (const l of lines) due(s, { at: s.tick + (f.every as number), k: 'rayStay', hid: h.id, x: l[0], y: l[1], n: [l[2], l[3], w, mulBp(dmg, f.bp as number), mulBp(heal, f.bp as number), (f.ticks as number) - (f.every as number), ...allies], ids: foes });
};

/** A pulse of a ray: hurts and heals what is on it; adds who it reached to `foes` and `allies`; returns what it healed. */
const rayPulse = (s: BattleState, h: Hero, l: readonly number[], w: number, dmg: number, heal: number, foes: number[], allies: number[]): number => {
  const [x0 = 0, y0 = 0, x1 = 0, y1 = 0] = l;
  for (const o of enemiesInLine(s, x0, y0, x1, y1, w)) {
    if (o.kind === 'boneWall') continue;
    damageEnemy(s, o, dmg, { pid: h.pid, type: 'holy' });
    if (!foes.includes(o.id)) foes.push(o.id);
  }
  let healed = 0;
  for (const o of heroesInLine(s, x0, y0, x1, y1, w)) {
    if (o.id === h.id) continue;
    healed += healHero(s, o, heal, h.pid);
    if (!allies.includes(o.id)) allies.push(o.id);
  }
  return healed;
};

const rayStay = (s: BattleState, h: Hero, d: UqDue): void => {
  const [x1 = 0, y1 = 0, w = 0, dmg = 0, heal = 0, left = 0, ...allies] = d.n ?? [];
  const foes = [...(d.ids ?? [])];
  featUse(s, h, 'watercarrierStaff', rayPulse(s, h, [d.x, d.y, x1, y1], w, dmg, heal, foes, allies));
  ev(s, { t: 'beam', x: d.x, y: d.y, tx: x1, ty: y1, kind: 'rayStay' });
  const every = fx('watercarrierStaff', h).every as number;
  if (left > every) {
    due(s, { ...d, at: s.tick + every, n: [x1, y1, w, dmg, heal, left - every, ...allies], ids: foes });
    return;
  }
  if (foes.length >= FEAT.need.rayFoes) featPieceAlly(s, h, 'watercarrierStaff', s.heroes.filter((o) => allies.includes(o.id)));
};

/* ---------- Bastón del farolero (Luz punzante) ---------- */

/** The lit one died: the light jumps on (next tick) with what it hurt and healed (`dmg`, `heal`). */
export const lightFollows = (s: BattleState, h: Hero, e: Enemy | null, dmg: number, heal: number): void => {
  if (!e || e.hp > 0 || !hasUq(h, 'lamplighterStaff')) return;
  due(s, { at: s.tick + 1, k: 'lightJump', hid: h.id, x: e.x, y: e.y, n: [dmg, heal, 1], ids: [e.id] });
};

const lightJump = (s: BattleState, h: Hero, d: UqDue): void => {
  const [dmg = 0, heal = 0, hop = 1] = d.n ?? [];
  const f = fx('lamplighterStaff', h);
  const o = nearestBut(s, d.x, d.y, f.range as number, d.ids ?? []);
  if (!o) return;
  damageEnemy(s, o, mulBp(dmg, f.bp as number), { pid: h.pid, type: 'holy' });
  ev(s, { t: 'beam', x: d.x, y: d.y, tx: o.x, ty: o.y, kind: 'lightJump' });
  const a = mostHurt(s, h.x, h.y, SKILLS.piercingLight.radius);
  if (a) {
    healHero(s, a, mulBp(heal, f.bp as number), h.pid);
    if (formFinal(h, 'lamplighterStaff')) cleanOne(a);
    ev(s, { t: 'beam', x: o.x, y: o.y, tx: a.x, ty: a.y, kind: 'faithRay' });
  }
  featUse(s, h, 'lamplighterStaff', 1);
  if (hop === FEAT.need.lightJumps) featPieceTest(s, h, 'lamplighterStaff');
  if (o.hp <= 0 && hop < (f.jumps as number)) due(s, { at: s.tick + 1, k: 'lightJump', hid: h.id, x: o.x, y: o.y, n: [dmg, heal, hop + 1], ids: [...(d.ids ?? []), o.id] });
};

/* ---------- Bastón de la partera (Renovar) ---------- */

/** The renewed keep whose renewal it is (and how long it was, for its passing on). */
export const renewedBy = (h: Hero, who: Hero[], ticks: number): void => {
  if (!hasUq(h, 'midwifeStaff')) return;
  for (const o of who) {
    const q = kept(o);
    q.renewBy = h.id;
    q.renewT = ticks;
    delete q.renewP;
  }
};

/** A blow on a renewed one: below `belowBp` the renewal bursts, all it had left at once. */
const renewBurst = (s: BattleState, h: Hero): void => {
  const q = h.uqT as Record<string, number>;
  const o = heroById(s, q.renewBy as number);
  if (!o || !hasUq(o, 'midwifeStaff') || !((h.renew ?? 0) > 0)) return;
  const f = fx('midwifeStaff', o);
  if (!below(h, f.belowBp as number)) return;
  delete q.renewBy;
  const left = (idiv(h.renew ?? 0, 12) + 1) * mulBp(h.stats.maxHp, h.renewBp ?? 0);
  h.renew = 0;
  const low = below(h, FEAT.need.saveBp);
  const healed = healHero(s, h, mulBp(left, f.bp as number), o.pid);
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 700, kind: 'renewBurst' });
  featUse(s, o, 'midwifeStaff', healed);
  if (low && healed > 0 && h.hp > 0) featPieceWatch(s, o, 'midwifeStaff', [h.id], FEAT.watch.midwife, -1);
};

/** The renewal ran out (heroes.ts): Renovar que pasa, to the most hurt near, for half the time (once). */
export const renewEnded = (s: BattleState, h: Hero): void => {
  const q = h.uqT as Record<string, number>;
  const by = q.renewBy;
  delete q.renewBy;
  if (by === undefined || q.renewP) return;
  const o = heroById(s, by);
  if (!o || !formFinal(o, 'midwifeStaff')) return;
  const ff = formFx('midwifeStaff', 'final');
  const next = mostHurt(s, h.x, h.y, ff.range as number, h.id);
  if (!next) return;
  next.renew = mulBp(q.renewT ?? 0, ff.bp as number);
  next.renewBp = h.renewBp;
  const nq = kept(next);
  nq.renewBy = by;
  nq.renewT = next.renew;
  nq.renewP = 1;
  ev(s, { t: 'beam', x: h.x, y: h.y, tx: next.x, ty: next.y, kind: 'renewPass' });
};

/* ---------- Hábito del hortelano (Tótem de guardia) ---------- */

/** The cleric whose totem leaps (the gardener's habit), or none. */
export const totemOwner = (s: BattleState, z: Zone): Hero | undefined => s.heroes.find((h) => h.pid === z.pid && hasUq(h, 'gardenerHabit'));

/**
 * The totem each tick: every `every` it leaps beside the ally with most nightmares around (not one another of its
 * totems is by); the allies it guarded are kept, and counted when it ends.
 */
export const totemTick = (s: BattleState, g: Hero, z: Zone): void => {
  const f = fx('gardenerHabit', g);
  const ids = (z.uqIds ??= []);
  if (z.ticks > 0 && z.ticks % (f.every as number) === 0) {
    let best: Hero | null = null;
    let bn = 0;
    for (const o of s.heroes) {
      if (o.downTicks > 0) continue;
      if (s.zones.some((x) => x !== z && x.kind === 'totem' && x.pid === z.pid && x.ticks > 0 && withinRange(x.x, x.y, o.x, o.y, 600))) continue;
      const n = enemiesNear(s, o.x, o.y, f.reach as number, (e) => targetable(s, e)).length;
      if (n > bn) {
        bn = n;
        best = o;
      }
    }
    if (best && !withinRange(best.x, best.y, z.x, z.y, 300)) {
      ev(s, { t: 'beam', x: z.x, y: z.y, tx: best.x, ty: best.y, kind: 'totemLeap' });
      z.x = best.x;
      z.y = best.y;
    }
  }
  for (const o of s.heroes) if (o.downTicks === 0 && !ids.includes(o.id) && withinRange(z.x, z.y, o.x, o.y, z.radius)) ids.push(o.id);
  if (z.ticks <= 0) featPieceAlly(s, g, 'gardenerHabit', s.heroes.filter((o) => ids.includes(o.id)));
};

/** What a gardener's totem spared an ally (combat.ts, its ward). */
export const totemSpared = (s: BattleState, h: Hero, n: number): void => {
  for (const z of s.zones) {
    if (z.kind !== 'totem' || !withinRange(z.x, z.y, h.x, h.y, z.radius)) continue;
    const g = totemOwner(s, z);
    if (g) {
      featUse(s, g, 'gardenerHabit', n);
      return;
    }
  }
};

/* ---------- Estola de la cisterna (Purificar) ---------- */

/**
 * What the cleanse is about to take from each ally falls on the nightmare nearest it: a stun, fear or sleep as a piece's
 * stun, poison as poison, a curse as hitting less. The rest stays gone. Agua bendita leaves holy ground.
 */
export const returnStates = (s: BattleState, h: Hero, who: Hero[], r: number): void => {
  if (!hasUq(h, 'cisternStole')) return;
  const f = fx('cisternStole', h);
  let n = 0;
  for (const a of who) {
    const e = nearestBut(s, a.x, a.y, f.range as number, []);
    if (!e) continue;
    let back = 0;
    let held = 0;
    for (const t of [a.stun, a.fear, a.sleep]) if ((t ?? 0) > 0) held++;
    if (held) {
      const st = pieceStun(e, f.stun as number);
      if (st > e.stun) e.stun = st;
      back += held;
    }
    if ((a.poison ?? 0) > 0) {
      poison(e, a.poisonDmg ?? 0, 12, a.poison ?? 0, h.pid);
      back++;
    }
    if ((a.curse ?? 0) > 0) {
      const bp = e.elite || bigOne(e) ? 10000 - idiv(10000 - (f.curseBp as number), 2) : (f.curseBp as number);
      if ((e.blind ?? 0) < (f.curseTicks as number)) e.blind = f.curseTicks as number;
      e.blindBp = imin(e.blindBp ?? 10000, bp);
      back++;
    }
    if (back) ev(s, { t: 'beam', x: a.x, y: a.y, tx: e.x, ty: e.y, kind: 'stateBack' });
    n += back;
  }
  featUse(s, h, 'cisternStole', n);
  if (n >= FEAT.need.returned) featPieceTest(s, h, 'cisternStole');
  if (formFinal(h, 'cisternStole')) {
    const ff = formFx('cisternStole', 'final');
    holyGround(s, h, h.x, h.y, r, ff.ticks as number, ff.healBp as number);
  }
};

/* ---------- Sayo del peregrino (Plegaria compartida) ---------- */

/** For each ally the prayer healed, holy light on the nightmare nearest it (`bp`: how strong); Amén, again later. */
export const prayerLights = (s: BattleState, h: Hero, who: Hero[], bp: number, healBp: number): void => {
  if (!hasUq(h, 'pilgrimSmock')) return;
  const f = fx('pilgrimSmock', h);
  const blow = mulBp(power(h, f.damage as number), bp);
  let dealt = 0;
  let kills = 0;
  for (const a of who) {
    const e = nearestBut(s, a.x, a.y, f.range as number, []);
    if (!e) continue;
    dealt += damageEnemy(s, e, blow, { pid: h.pid, type: 'holy' });
    if (e.hp <= 0) kills++;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 400, kind: 'prayerLight' });
  }
  featUse(s, h, 'pilgrimSmock', dealt);
  if (kills >= FEAT.need.prayerKills) featPieceTest(s, h, 'pilgrimSmock');
  if (bp === 10000 && formFinal(h, 'pilgrimSmock')) {
    const ff = formFx('pilgrimSmock', 'final');
    due(s, { at: s.tick + (ff.after as number), k: 'prayerAgain', hid: h.id, x: h.x, y: h.y, n: [mulBp(healBp, ff.bp as number), ff.bp as number] });
  }
};

const prayerAgain = (s: BattleState, h: Hero, d: UqDue): void => {
  const [healBp = 0, bp = 0] = d.n ?? [];
  const who = s.heroes.filter((o) => o.downTicks === 0 && withinRange(o.x, o.y, h.x, h.y, SKILLS.sharedPrayer.radius));
  for (const o of who) healHero(s, o, mulBp(o.stats.maxHp, healBp), h.pid);
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 1500, kind: 'prayerAgain' });
  prayerLights(s, h, who, bp, healBp);
};

/* ---------- Sol de bronce (Juicio del alba) ---------- */

/** The column walks on from where it fell (`dmg`: the judgment's blow; `hits`: what it struck). */
export const columnWalks = (s: BattleState, h: Hero, x: number, y: number, r: number, dmg: number, hits: Enemy[]): void => {
  if (!hasUq(h, 'bronzeSun')) return;
  const f = fx('bronzeSun', h);
  const kills = hits.filter((o) => o.hp <= 0).length;
  due(s, { at: s.tick + (f.every as number), k: 'columnStep', hid: h.id, x, y, n: [mulBp(dmg, f.damageBp as number), r, (f.ticks as number) - (f.every as number), kills] });
};

const columnStep = (s: BattleState, h: Hero, d: UqDue): void => {
  const [dmg = 0, r = 0, left = 0, kills0 = 0] = d.n ?? [];
  const f = fx('bronzeSun', h);
  // Toward the biggest around it.
  let big: Enemy | null = null;
  for (const o of enemiesNear(s, d.x, d.y, f.range as number, (e) => targetable(s, e) && e.kind !== 'boneWall')) if (!big || o.maxHp > big.maxHp || (o.maxHp === big.maxHp && o.id < big.id)) big = o;
  let x = d.x;
  let y = d.y;
  if (big) {
    const dd = isqrt(dist2(big.x, big.y, x, y));
    const k = imin(f.step as number, dd);
    if (dd > 0) {
      x += idiv((big.x - x) * k, dd);
      y += idiv((big.y - y) * k, dd);
    }
  }
  let dealt = 0;
  let kills = kills0;
  for (const o of enemiesNear(s, x, y, r, (e) => targetable(s, e))) {
    dealt += damageEnemy(s, o, dmg, { pid: h.pid, type: 'holy' });
    if (o.hp <= 0) kills++;
  }
  ev(s, { t: 'boom', x, y, r, kind: 'dawnColumn' });
  featUse(s, h, 'bronzeSun', dealt);
  const every = f.every as number;
  if (left > 0) {
    due(s, { at: s.tick + every, k: 'columnStep', hid: h.id, x, y, n: [dmg, r, left - every, kills] });
    return;
  }
  if (kills >= FEAT.need.columnKills) featPieceTest(s, h, 'bronzeSun');
  if (formFinal(h, 'bronzeSun')) holyGround(s, h, x, y, r, formFx('bronzeSun', 'final').ticks as number, 10000);
};

/* ---------- Campanilla de la enfermería (Coro) ---------- */

/** The choir sung with the bell: each ally keeps whose it is; the cleric, how much it may still grow. */
export const choirBy = (h: Hero, who: Hero[], ticks: number): void => {
  if (!hasUq(h, 'infirmaryBell')) return;
  for (const o of who) kept(o).choirBy = h.id;
  const q = kept(h);
  q.chCap = mulBp(ticks, fx('infirmaryBell', h).capBp as number);
  q.chAdd = 0;
  q.chOn = 1;
};

/** A choir heal (kits.ts) on an ally with the bell's choir: counted, and its second kept. */
export const choirHealed = (s: BattleState, h: Hero, got: number): void => {
  const q = h.uqT as Record<string, number>;
  const o = heroById(s, q.choirBy as number);
  if (!o || !hasUq(o, 'infirmaryBell')) return;
  q.choirAt = s.tick;
  featUse(s, o, 'infirmaryBell', got);
};

/** Each second: every ally the choir healed in it lengthens it for all; its end, with Último canto, raises one. */
const choirTick = (s: BattleState, h: Hero, q: Record<string, number>): void => {
  if (s.tick % 12 !== 0) return;
  let on = 0;
  let healed = 0;
  for (const o of s.heroes) {
    if (o.uqT?.choirBy !== h.id || !((o.choir ?? 0) > 0)) continue;
    on++;
    if ((o.uqT.choirAt ?? -99) > s.tick - 12) healed++;
  }
  if (!on) {
    q.chOn = 0;
    if (formFinal(h, 'infirmaryBell') && h.downTicks === 0) lastSong(s, h);
    return;
  }
  const add = imin(healed * (fx('infirmaryBell', h).perAlly as number), (q.chCap ?? 0) - (q.chAdd ?? 0));
  if (add <= 0) return;
  for (const o of s.heroes) if (o.uqT?.choirBy === h.id && (o.choir ?? 0) > 0) o.choir = (o.choir ?? 0) + add;
  q.chAdd = (q.chAdd ?? 0) + add;
  if (q.chAdd >= (q.chCap ?? 0)) featPieceTest(s, h, 'infirmaryBell');
};

const lastSong = (s: BattleState, h: Hero): void => {
  const ff = formFx('infirmaryBell', 'final');
  const o = s.heroes.find((x) => x.downTicks > 0 && withinRange(x.x, x.y, h.x, h.y, ff.radius as number));
  if (!o) return;
  getUp(s, o, ff.hpBp as number);
  ev(s, { t: 'boom', x: o.x, y: o.y, r: 600, kind: 'revive' });
};

/* ---------- Medalla del velador (Intercesión) ---------- */

/** The intercession's mark keeps whose it is (and how long, for Segunda marca). */
export const vigilMark = (h: Hero, a: Hero): void => {
  if (!hasUq(h, 'vigilMedal')) return;
  const q = kept(a);
  q.icBy = h.id;
  q.icT = a.intercede ?? 0;
  delete q.icP;
};

/** The marked one got up (combat.ts heroDown): light stuns around and heals the allies there; Segunda marca, once. */
export const vigilRaised = (s: BattleState, h: Hero): void => {
  const q = h.uqT as Record<string, number>;
  const by = q.icBy as number;
  delete q.icBy;
  const o = heroById(s, by);
  if (!o || !hasUq(o, 'vigilMedal')) return;
  const f = fx('vigilMedal', o);
  let n = 0;
  for (const e of enemiesNear(s, h.x, h.y, f.radius as number, (x) => targetable(s, x))) {
    const st = pieceStun(e, f.stun as number);
    if (st > e.stun) e.stun = st;
    if (st > 0) n++;
  }
  for (const a of s.heroes) if (a.downTicks === 0 && withinRange(a.x, a.y, h.x, h.y, f.radius as number)) healHero(s, a, mulBp(a.stats.maxHp, f.healBp as number), o.pid);
  ev(s, { t: 'boom', x: h.x, y: h.y, r: f.radius as number, kind: 'vigilLight' });
  featUse(s, o, 'vigilMedal', 1);
  if (n >= FEAT.need.medalStun) featPieceWatch(s, o, 'vigilMedal', [h.id], FEAT.watch.medal, -1);
  if (q.icP || !formFinal(o, 'vigilMedal')) return;
  const next = mostHurt(s, h.x, h.y, SKILLS.intercession.castRange, h.id);
  if (!next) return;
  next.intercede = q.icT ?? 0;
  next.intercedeBp = h.intercedeBp;
  const nq = kept(next);
  nq.icBy = by;
  nq.icT = next.intercede;
  nq.icP = 1;
  ev(s, { t: 'beam', x: h.x, y: h.y, tx: next.x, ty: next.y, kind: 'markPass' });
};

/* ---------- each tick, the blows, and the queue ---------- */

/** A blow on a hero who keeps something of a cleric's piece (a renewal that bursts). */
export const clericHurt = (s: BattleState, h: Hero): void => {
  if (h.uqT?.renewBy !== undefined) renewBurst(s, h);
};

/** Every tick, for a cleric wearing a piece: the bell's choir. */
export const clericHeroTick = (s: BattleState, h: Hero): void => {
  const q = h.uqT as Record<string, number>;
  if (q.chOn) choirTick(s, h, q);
};

/** The cleric's later effects; false if `d` isn't one. */
export const clericDue = (s: BattleState, h: Hero, d: UqDue): boolean => {
  if (d.k === 'rayStay') rayStay(s, h, d);
  else if (d.k === 'lightJump') lightJump(s, h, d);
  else if (d.k === 'prayerAgain') prayerAgain(s, h, d);
  else if (d.k === 'columnStep') columnStep(s, h, d);
  else return false;
  return true;
};
